/* React base class */
import { Component } from "react"

/* Redux store connection */
import { connect } from "react-redux"

/* Router HOC and shared utilities */
import { withRouter, utils, applySeo } from "../helpers"

/* Shared UI components */
import { Block, Card, Loading, Network } from "../components"

/* Icon components */
import Icon from "../components/Icon"
import CatIcon from "../components/CatIcon"

/* Hero and promo modals */
import CityModal from "../components/CityModal"
import ServicePickerModal from "../components/ServicePickerModal"
import WhenModal from "../components/WhenModal"
import CertificateModal from "../components/CertificateModal"
import BookingModal from "../components/BookingModal"

/* API: featured home collections, nearby search and "repeat booking" services */
import { featured, list } from "../api/Organization"
import { repeatBookings } from "../api/Me"

/* i18n */
import { translate } from "../translates"

/* Fallback icon slug by category name (used when a category has no image) */
const NAME_SLUG = {
    "Маникюр": "nails", "Наращивание ногтей": "ext", "Педикюр": "pedi", "Снятие покрытия": "remove", "Подология": "podo",
    "Стрижка волос": "haircut", "Укладка волос": "styling", "Брейдинг": "braiding", "Окрашивание": "color",
    "Наращивание ресниц": "lashes", "Брови": "brows", "Ламинирование": "lami",
    "Массаж": "massage", "Косметология": "face", "SPA": "spa",
    "Автомойка": "wash", "Шиномонтаж": "tires", "СТО · ремонт": "sto", "Детейлинг": "detail", "Полировка": "polish"
}

/* Quick popular-search chips */
const CHIPS = ["Маникюр", "Стрижка", "Окрашивание", "Автомойка", "Шиномонтаж", "Массаж"]

/* Steps for the "how it works" block */
const STEPS = [
    { number: "01", title: "Найдите услугу", text: "Выберите услугу, район и удобное время." },
    { number: "02", title: "Выберите мастера", text: "Сравните цены, рейтинги и отзывы." },
    { number: "03", title: "Запишитесь онлайн", text: "Получите подтверждение и напоминание." }
]

/* Page Home */
class Home extends Component {

    constructor(props) {
        super(props)

        /* State */
        this.state = {
            loading: true,
            error: false,
            sections: [],
            repeat: [],
            service: "Маникюр",
            serviceCategory: null,
            when: "Сегодня",
            serviceOpen: false,
            whenOpen: false,
            cityOpen: false,
            catOpen: false,
            certOpen: false,
            bookingRepeat: null,
            nearby: []
        }
    }

    componentDidMount = () => {

        /* SEO */
        applySeo({
            title: "Bookify — онлайн-запись в салоны красоты, барбершопы и сервисы Казахстана",
            description: "Онлайн-запись в лучшие салоны красоты, барбершопы, студии маникюра, автомойки и шиномонтажи Казахстана. Выбирайте мастера и время, читайте отзывы и записывайтесь за 30 секунд.",
            keywords: "онлайн запись, запись к мастеру, салоны красоты Казахстан, барбершоп, маникюр, педикюр, автомойка, шиномонтаж, записаться онлайн Алматы Астана, Bookify",
            path: "/"
        })

        /* Scroll */
        window.scroll(0, 0)

        /* Load */
        this.load()
        this._loadNearby()
    }

    componentDidUpdate = (prev) => {

        /* Reload featured and nearby when the city changes */
        const prevCityId = prev.city ? prev.city.id : null
        const nextCityId = this.props.city ? this.props.city.id : null
        if (prevCityId !== nextCityId) {
            this.load()
            this._loadNearby()
        }

        /* Refresh the "repeat" block on sign in/out */
        if (prev.token !== this.props.token) {
            this._loadRepeat()
        }
    }

    /* Draw Load */
    load = () => {

        /* Fields */
        const cityId = this.props.city ? this.props.city.id : null

        /* Fetch featured collections */
        this.setState({ loading: true, error: false })
        featured(cityId).then((response) => {
            if (response && response.status === 200) {
                this.setState({ sections: response.data.sections || [], loading: false })
            }
            else {
                this.setState({ loading: false, error: true })
            }
        })

        this._loadRepeat()
    }

    /* Draw Load Repeat */
    _loadRepeat = () => {

        /* Fields */
        const { token } = this.props

        /* Guests have no repeat block */
        if (!token) {
            this.setState({ repeat: [] })
            return
        }
        repeatBookings(token).then((response) => {
            if (response && response.status === 200 && Array.isArray(response.data)) {
                this.setState({ repeat: response.data })
            }
        })
    }

    /* Draw Load Nearby */
    _loadNearby = () => {

        /* Fetch nearby organizations when geolocation is available (prioritized over the popular promo) */
        if (!navigator.geolocation) {
            return
        }
        navigator.geolocation.getCurrentPosition((position) => {
            const params = { sort: "near", lat: position.coords.latitude, lng: position.coords.longitude, limit: 8 }
            if (this.props.city) {
                params.city = this.props.city.id
            }
            list(params).then((response) => {
                if (response && response.status === 200 && response.data && Array.isArray(response.data.data)) {
                    this.setState({ nearby: response.data.data })
                }
            })
        }, () => {}, { timeout: 5000 })
    }

    /* Draw Open Cert */
    _openCert = () => {

        /* Require sign in before buying a certificate */
        if (!this.props.token) {
            this.props.toggleAuth(true)
            return
        }
        this.setState({ certOpen: true })
    }

    /* Draw Search */
    _search = () => {

        /* Fields */
        const { serviceCategory, service, when } = this.state
        const { data } = this.props
        const categories = data && Array.isArray(data.categories) ? data.categories : []

        /* Prefer the real category id so results filter by service (not just by name) */
        const matched = serviceCategory || categories.find((category) => category.name === service)
        const params = new URLSearchParams()
        if (matched && matched.id) {
            params.set("category", matched.id)
        }
        else if (service) {
            params.set("q", service)
        }
        if (when) {
            params.set("when", when)
        }
        this.props.navigate(`/search?${params.toString()}`)
    }

    /* Draw Money */
    _money = (value) => `${Number(value || 0).toLocaleString("ru-RU")} ₸`

    /* Draw Popular Org */
    _popularOrg = () => {

        /* Highest-rated organization across all featured sections — used for the promo */
        let best = null
        this.state.sections.forEach((section) => {
            (Array.isArray(section.organizations) ? section.organizations : []).forEach((organization) => {
                if (!best || (organization.rating || 0) > (best.rating || 0)) {
                    best = organization
                }
            })
        })
        return best
    }

    /* Draw Category Icon */
    _categoryIcon = (category) => {

        /* Website-specific illustration first, then the shared image, then a line icon */
        const picture = category.webImage || category.image
        if (picture) {
            return <img src={utils.media(`categories/${encodeURIComponent(picture)}`)} alt="" onError={utils.onImgError} />
        }
        return <CatIcon cat={category.slug || NAME_SLUG[category.name]} size={30} />
    }

    /* Draw Hero */
    _hero = () => {

        /* Fields */
        const { service, when } = this.state
        const city = this.props.city ? this.props.city.name : translate("Алматы")

        return (
            <section className="hero">
                <div className="container hero__inner">
                    <div className="hero__top">
                        <div className="hero__content">
                            <span className="hero__eyebrow">
                                <Icon name="star" size={12} fill="currentColor" stroke="none" />
                                {translate("200+ салонов, мастерских и автосервисов")}
                            </span>
                            <h1>{translate("Запись и")} <em>{translate("живая очередь")}</em> {translate("— за пару кликов")}</h1>
                            <p className="hero__lede">
                                {translate("Маникюр, стрижка, ресницы, массаж, автомойка, шиномонтаж и СТО — бронируйте онлайн или вставайте в живую очередь по геолокации.")}
                            </p>

                            <div className="hero__search">
                                <button className="hero__field" onClick={() => this.setState({ serviceOpen: true })}>
                                    <div className="hero__field-label">{translate("Услуга")}</div>
                                    <div className="hero__field-input">{translate(service)}</div>
                                </button>
                                <button className="hero__field" onClick={() => this.setState({ cityOpen: true })}>
                                    <div className="hero__field-label">{translate("Город")}</div>
                                    <div className="hero__field-input">{city}</div>
                                </button>
                                <button className="hero__field" onClick={() => this.setState({ whenOpen: true })}>
                                    <div className="hero__field-label">{translate("Когда")}</div>
                                    <div className="hero__field-input">{translate(when)}</div>
                                </button>
                                <button className="hero__cta" onClick={this._search}>
                                    <Icon name="search" size={18} />
                                    {translate("Найти")}
                                </button>
                            </div>

                            <div className="hero__chips">
                                <span className="hero__chips-label">{translate("Популярно:")}</span>
                                {CHIPS.map((chip) => (
                                    <button key={chip} className="chip" onClick={() => this.props.navigate(`/search?q=${encodeURIComponent(chip)}`)}>{translate(chip)}</button>
                                ))}
                            </div>
                        </div>

                        <div className="hero__visual">
                            <div className="hero__visual-main">
                                <img src="https://images.unsplash.com/photo-1632345031435-8727f6897d53?w=900&q=80" alt="" />
                            </div>
                            <div className="hero__visual-side">
                                <img src="https://images.unsplash.com/photo-1601362840469-51e4d8d58785?w=600&q=80" alt="" />
                            </div>
                            <div className="hero__badge hero__badge--booked">
                                <div className="hero__badge-icon"><Icon name="clock" size={18} /></div>
                                <div className="hero__badge-text">
                                    <div className="hero__badge-num">{translate("Очередь · 3 чел")}</div>
                                    <div className="hero__badge-label">{translate("шиномонтаж рядом")}</div>
                                </div>
                            </div>
                            <div className="hero__badge hero__badge--time">
                                <div className="hero__badge-icon hero__badge-icon--success"><Icon name="calendar" size={18} /></div>
                                <div className="hero__badge-text">
                                    <div className="hero__badge-num">{translate("Запись · 18:30")}</div>
                                    <div className="hero__badge-label">{translate("сегодня, маникюр")}</div>
                                </div>
                            </div>
                            <div className="hero__badge hero__badge--rating">
                                <div className="hero__badge-avatars">
                                    <span className="hero__badge-avatar"><img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&q=80" alt="" /></span>
                                    <span className="hero__badge-avatar"><img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&q=80" alt="" /></span>
                                    <span className="hero__badge-avatar"><img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=80&q=80" alt="" /></span>
                                </div>
                                <div className="hero__badge-text">
                                    <div className="hero__badge-num hero__badge-num--rating">
                                        4.9 <Icon name="star" size={14} fill="var(--star)" stroke="none" />
                                    </div>
                                    <div className="hero__badge-label">{translate("12 320 отзывов")}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="hero__stats">
                        <div><div className="hero__stat-num">200+</div><div className="hero__stat-label">{translate("салонов и сервисов")}</div></div>
                        <div><div className="hero__stat-num">3 000+</div><div className="hero__stat-label">{translate("записей в месяц")}</div></div>
                        <div><div className="hero__stat-num">4.8 ★</div><div className="hero__stat-label">{translate("средний рейтинг")}</div></div>
                        <div><div className="hero__stat-num">{translate("2 мин")}</div><div className="hero__stat-label">{translate("занимает запись")}</div></div>
                    </div>
                </div>
            </section>
        )
    }

    /* Draw Promo */
    _promo = () => {

        /* Fields */
        const popular = this._popularOrg()
        const popularImage = popular && popular.image ? utils.media(popular.image) : "https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?w=1200&q=80"
        const popularEyebrow = popular && popular.tags && popular.tags.length ? popular.tags[0] : translate("Популярное заведение")
        const popularDescription = popular && popular.addr ? popular.addr : translate("Уникальный подход, качественный уход и тёплая атмосфера.")
        const openPopular = () => popular ? this.props.navigate(`/organization/${popular.url || popular.id}`) : this.props.navigate("/search")

        return (
            <section className="section section--tight">
                <div className="container">
                    <div className="promo-grid">
                        <div className="promo promo--big promo--link" onClick={openPopular}>
                            <img className="promo__img" src={popularImage} alt="" onError={utils.onImgError} />
                            <span className="promo__badge"><Icon name="star" size={12} fill="currentColor" stroke="none" /> {translate("Популярное")}</span>
                            <span className="promo__eyebrow">{popularEyebrow}</span>
                            <div>
                                <h3>{popular ? popular.name : translate("Найдите своё место")}</h3>
                                <p>{popularDescription}</p>
                            </div>
                            <button className="promo__cta" onClick={(event) => { event.stopPropagation(); openPopular() }}>
                                {translate("Записаться")} <Icon name="arrow_right" size={14} />
                            </button>
                        </div>
                        <div className="promo promo--link" onClick={this._openCert}>
                            <img className="promo__img" src="https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=900&q=80" alt="" />
                            <span className="promo__eyebrow">{translate("Подарок близким")}</span>
                            <div>
                                <h3>{translate("Сертификаты")}</h3>
                                <p>{translate("Электронные сертификаты от 5 000 ₸ — на любую услугу")}</p>
                            </div>
                            <button className="promo__cta" onClick={(event) => { event.stopPropagation(); this._openCert() }}>{translate("Купить")}</button>
                        </div>
                        <div className="promo promo--pro">
                            <span className="promo__eyebrow">{translate("Для бизнеса")}</span>
                            <div>
                                <div className="prologo">
                                    <span className="prologo__word">bookify</span>
                                    <span className="prologo__pro">PRO</span>
                                </div>
                                <p>{translate("CRM и онлайн-запись для салонов")}</p>
                            </div>
                            <button className="promo__cta" onClick={() => window.open("/partnership", "_blank")}>{translate("Стать партнёром")}</button>
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    /* Draw Categories */
    _categories = () => {

        /* Fields */
        const categories = this.props.data && Array.isArray(this.props.data.categories) ? this.props.data.categories : []

        /* Nothing to show */
        if (!categories.length) {
            return null
        }

        return (
            <section className="section section--tight">
                <div className="container">
                    <div className="section__head">
                        <div>
                            <h2>{translate("Категории услуг")}</h2>
                            <p>{translate("Выберите, что нужно сделать")}</p>
                        </div>
                        <button className="section__head-link" onClick={() => this.setState({ catOpen: true })}>
                            {translate("Все категории")} <Icon name="arrow_right" size={14} />
                        </button>
                    </div>
                    <div className="caticons">
                        {categories.map((category) => (
                            <button key={`${category.id}`} className="caticon" onClick={() => this.props.navigate(`/search?category=${category.id}`)}>
                                <span className="caticon__circle">{this._categoryIcon(category)}</span>
                                <span className="caticon__label">{translate(category.name)}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </section>
        )
    }

    /* Draw Repeat */
    _repeat = () => {

        /* Fields */
        const { repeat } = this.state

        /* Empty */
        if (!repeat.length) {
            return null
        }

        return (
            <section className="section section--tight">
                <div className="container">
                    <div className="section__head">
                        <div>
                            <h2>{translate("Повторить запись")}</h2>
                            <p>{translate("Услуги, на которые вы записывались раньше")}</p>
                        </div>
                        <button className="section__head-link" onClick={() => this.props.navigate("/bookings")}>
                            {translate("Все мои записи")} <Icon name="arrow_right" size={14} />
                        </button>
                    </div>
                    <div className="repeat-grid">
                        {repeat.map((item, index) => (
                            <div key={`${index}`} className="repeat" onClick={() => this.props.navigate(`/organization/${item.organizationId}`)}>
                                <img className="repeat__img" src={utils.media(item.organizationImage) || utils.FALLBACK_IMG} alt="" onError={utils.onImgError} />
                                <div className="repeat__body">
                                    <div className="repeat__last"><Icon name="clock" size={12} /> {translate("Последний визит")} · {utils.shortDate(item.lastVisit)}</div>
                                    <div className="repeat__organization">{item.organizationName}</div>
                                    <div className="repeat__service">{translate(item.service)} · {utils.duration(item.duration)}</div>
                                    <div className="repeat__bottom">
                                        <div className="repeat__price">{translate("от")} {this._money(item.price)}</div>
                                        <button className="btn repeat__btn" onClick={(event) => { event.stopPropagation(); this.setState({ bookingRepeat: item }) }}>
                                            <Icon name="arrow_right" size={14} />
                                            {translate("Повторить")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        )
    }

    /* Draw Nearby */
    _nearby = () => {

        /* Fields */
        const { nearby } = this.state

        /* Only when geolocation returned nearby organizations */
        if (!nearby.length) {
            return null
        }

        return (
            <section className="section section--tight">
                <div className="container">
                    <div className="section__head">
                        <div>
                            <h2>{translate("Рядом с вами")}</h2>
                            <p>{translate("Ближайшие заведения по вашему местоположению")}</p>
                        </div>
                        <button className="section__head-link" onClick={() => this.props.navigate("/search")}>
                            {translate("Смотреть все")} <Icon name="arrow_right" size={14} />
                        </button>
                    </div>
                    <div className="grid grid--4">
                        {nearby.slice(0, 4).map((organization) => (
                            <Card key={`${organization.id}`} s={organization} />
                        ))}
                    </div>
                </div>
            </section>
        )
    }

    /* Draw Carousels */
    _carousels = () => {

        /* Fields */
        const { sections, nearby } = this.state
        const popular = this._popularOrg()
        const seen = new Set(popular ? [popular.id] : [])

        /* Nearby organizations already have their own section — don't repeat them in the carousels */
        nearby.forEach((organization) => seen.add(organization.id))

        /* Allocate the rarest categories first so abundant ones (e.g. Маникюр) don't starve them of organizations */
        const order = sections.map((section, index) => index).sort((a, b) => (sections[a].total || 0) - (sections[b].total || 0))
        const picked = {}
        order.forEach((index) => {
            const all = Array.isArray(sections[index].organizations) ? sections[index].organizations : []
            const chosen = all.filter((organization) => !seen.has(organization.id)).slice(0, 4)
            chosen.forEach((organization) => seen.add(organization.id))
            picked[index] = chosen
        })

        return sections.map((section, index) => {
            const organizations = picked[index] || []
            if (!organizations.length) {
                return null
            }
            return (
                <section className="section section--tight" key={`${index}`}>
                    <div className="container">
                        <div className="section__head">
                            <div>
                                <h2>{translate(section.label)}</h2>
                                {section.sub && <p>{translate(section.sub)} · {this.props.city ? this.props.city.name : translate("Алматы")}</p>}
                            </div>
                            {section.categoryId && (
                                <button className="section__head-link" onClick={() => this.props.navigate(`/search?category=${section.categoryId}`)}>
                                    {translate("Смотреть все")} <Icon name="arrow_right" size={14} />
                                </button>
                            )}
                        </div>
                        <div className="grid grid--4">
                            {organizations.map((organization) => (
                                <Card key={`${organization.id}`} s={organization} />
                            ))}
                        </div>
                    </div>
                </section>
            )
        })
    }

    /* Draw How */
    _how = () => {
        return (
            <section className="section how">
                <div className="container">
                    <div className="section__head how__head">
                        <div className="how__head-inner">
                            <h2>{translate("Как работает Bookify")}</h2>
                            <p>{translate("Запись занимает меньше 2 минут")}</p>
                        </div>
                    </div>
                    <div className="grid grid--3">
                        {STEPS.map((step) => (
                            <div key={step.number} className="how__card">
                                <div className="how__num">{step.number}</div>
                                <h3 className="how__title">{translate(step.title)}</h3>
                                <p className="how__text">{translate(step.text)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        )
    }

    /* Draw App CTA */
    _appcta = () => {
        return (
            <section className="section section--tight">
                <div className="container">
                    <div className="appcta">
                        <div className="appcta__text">
                            <span className="appcta__eyebrow">{translate("Bookify в кармане")}</span>
                            <h2>{translate("Скачайте приложение")}</h2>
                            <p>{translate("Записывайтесь в пару касаний, получайте напоминания о визитах и эксклюзивные предложения салонов — прямо в телефоне.")}</p>
                            <div className="appcta__stores">
                                <a className="store" href="https://apps.apple.com/app/apple-store/id6741415952" target="_blank" rel="noreferrer" aria-label={translate("Скачать в App Store")}>
                                    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true"><path d="M16.5 1.5c.1 1-.3 2-1 2.8-.7.8-1.7 1.3-2.7 1.2-.1-1 .4-2 1-2.7.7-.8 1.8-1.3 2.7-1.3zM19.6 17c-.5 1.2-.8 1.7-1.4 2.8-.9 1.5-2.1 3.3-3.7 3.3-1.4 0-1.7-.9-3.6-.9-1.8 0-2.2.9-3.6.9-1.5 0-2.7-1.7-3.6-3.1C1.3 16.5.8 12.4 2.4 9.8c.9-1.3 2.3-2.1 3.6-2.1 1.4 0 2.3 1 3.5 1 1.1 0 1.8-1 3.5-1 1.2 0 2.5.7 3.4 1.9-3 1.6-2.5 5.9.2 7.4z" /></svg>
                                    <span><small>{translate("Загрузите в")}</small><b>App Store</b></span>
                                </a>
                                <a className="store" href="https://play.google.com/store/apps/details?id=kz.bookify.app" target="_blank" rel="noreferrer" aria-label={translate("Скачать в Google Play")}>
                                    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true"><path d="M3.6 2.3c-.2.2-.3.6-.3 1v17.4c0 .4.1.8.3 1l9.2-9.7L3.6 2.3zm10.3 8.3 2.9-3-9.6-5.5c-.4-.2-.8-.2-1.1-.1l7.8 8.6zm0 2.8L6.1 22c.3.1.7.1 1.1-.1l9.6-5.5-2.9-2.9zm5.5-3.2-2.1-1.2-3.2 3.3 3.2 3.2 2.1-1.2c.9-.5.9-2.4 0-2.9z" /></svg>
                                    <span><small>{translate("Доступно в")}</small><b>Google Play</b></span>
                                </a>
                            </div>
                        </div>
                        <div className="appcta__media">
                            <img src="/app-banner.png" alt={translate("Приложение Bookify")} />
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    render = () => {

        /* Fields */
        const { serviceOpen, whenOpen, cityOpen, catOpen, certOpen, bookingRepeat, service, when, loading, error } = this.state

        return (
            <Block>
                {serviceOpen && (
                    <ServicePickerModal
                        value={service}
                        onSelect={(category) => this.setState({ service: category.name, serviceCategory: category })}
                        onClose={() => this.setState({ serviceOpen: false })}
                    />
                )}
                {whenOpen && (
                    <WhenModal
                        value={when}
                        onSelect={(value) => this.setState({ when: value })}
                        onClose={() => this.setState({ whenOpen: false })}
                    />
                )}
                {cityOpen && (
                    <CityModal onClose={() => this.setState({ cityOpen: false })} />
                )}
                {catOpen && (
                    <ServicePickerModal
                        value=""
                        onSelect={(category) => this.props.navigate(`/search?category=${category.id}`)}
                        onClose={() => this.setState({ catOpen: false })}
                    />
                )}
                {certOpen && (
                    <CertificateModal onClose={() => this.setState({ certOpen: false })} />
                )}
                {bookingRepeat && (
                    <BookingModal repeat={bookingRepeat} onClose={() => this.setState({ bookingRepeat: null })} />
                )}
                {this._hero()}
                {this._nearby()}
                {this._promo()}
                {this._categories()}
                {this._repeat()}
                {loading ? (
                    <Loading />
                ) : error ? (
                    <Network onRetry={this.load} />
                ) : (
                    this._carousels()
                )}
                {this._how()}
                {this._appcta()}
            </Block>
        )
    }
}

/* Map State To Props */
const mapStateToProps = state => {
    return {
        token: state.token,
        city: state.city,
        data: state.data,
        lang: state.lang
    }
}

/* Map Dispatch To Props */
const mapDispatchToProps = dispatch => {
    return {
        toggleAuth: data => dispatch({ type: "TOGGLE_AUTH", payload: data })
    }
}

/* Export */
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Home))
